import React, { useEffect, useState, useCallback } from "react";
import api from "../apiClient";
import "./Modal.css";

export default function UserManagement({ onClose, userInfo }) {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Track per-action loading states
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersRes, invitesRes] = await Promise.all([
        api.get("/api/company/members"),
        api.get("/api/company/invites"),
      ]);
      setMembers(membersRes.data);
      setInvites(invitesRes.data);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const emailToSend = inviteEmail.trim();
    if (!emailToSend) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, sendInvite: true }));
      await api.post("/api/company/invite", {
        email: emailToSend,
        companyId: userInfo?.companyId,
      });
      setInviteEmail("");
      setSuccess(`Invitation sent to ${emailToSend}`);

      // Append new invite locally (optional: if API returns the invite)
      setInvites(prev => [...prev, { id: Date.now(), email: emailToSend, role: "member" }]);
    } catch (e) {
      const errorMessage = e?.response?.data?.error || e?.response?.data?.message || "Failed to send invite";
      setError(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, sendInvite: false }));
    }
  };

  const revokeInvite = async (inviteId) => {
    setError("");
    setSuccess("");
    try {
      setActionLoading(prev => ({ ...prev, [`revoke-${inviteId}`]: true }));
      const response = await api.delete(`/api/company/invite/${inviteId}`);
      if (response.status === 200) {
        setInvites(list => list.filter(i => i.id !== inviteId));
        setSuccess("Invite revoked successfully");
      }
    } catch (e) {
      console.error("Error revoking invite:", e);
      const errorMessage = e?.response?.data?.error || e?.response?.data?.message || "Failed to revoke invite";
      setError(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [`revoke-${inviteId}`]: false }));
    }
  };

  const changeRole = async (uid, role) => {
    setError("");
    setSuccess("");
    try {
      setActionLoading(prev => ({ ...prev, [`role-${uid}`]: true }));
      await api.post("/api/company/members/role", { uid, role });
      setMembers(ms => ms.map(m => (m.uid === uid ? { ...m, role } : m)));
      setSuccess("Role updated successfully");
    } catch (e) {
      const errorMessage = e?.response?.data?.error || e?.response?.data?.message || "Failed to change role";
      setError(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [`role-${uid}`]: false }));
    }
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member from the company?")) return;
    setError("");
    setSuccess("");
    try {
      setActionLoading(prev => ({ ...prev, [`remove-${uid}`]: true }));
      await api.delete(`/api/company/members/${uid}`);
      setMembers(ms => ms.filter(m => m.uid !== uid));
      setSuccess("Member removed successfully");
    } catch (e) {
      const errorMessage = e?.response?.data?.error || e?.response?.data?.message || "Failed to remove member";
      setError(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [`remove-${uid}`]: false }));
    }
  };

  if (loading) {
    return (
      <div className="management-overlay">
        <div className="management-modal">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading user data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-overlay">
      <div className="management-modal">
        <div className="modal-header">
          <h2>Manage Users</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {/* Members Section */}
          <section className="section">
            <h3>Company Members ({members.length})</h3>
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Email Verified</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(member => (
                    <tr key={member.uid}>
                      <td>{member.email}</td>
                      <td>
                        <select
                          className="role-select"
                          value={member.role}
                          onChange={(e) => changeRole(member.uid, e.target.value)}
                          disabled={member.uid === userInfo?.uid || actionLoading[`role-${member.uid}`]}
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td>
                        <span className={`status-badge ${member.email_verified ? "verified" : "unverified"}`}>
                          {member.email_verified ? "✓ Verified" : "⚠ Unverified"}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-danger"
                          onClick={() => removeMember(member.uid)}
                          disabled={member.uid === userInfo?.uid || actionLoading[`remove-${member.uid}`]}
                          title={member.uid === userInfo?.uid ? "You can't remove yourself" : "Remove user"}
                        >
                          {actionLoading[`remove-${member.uid}`] ? "Removing..." : "Remove"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {members.length === 0 && (
                    <tr>
                      <td colSpan="4" className="no-data">No members found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Invites Section */}
          <section className="section">
            <h3>Pending Invites ({invites.length})</h3>

            <form className="invite-form" onSubmit={sendInvite}>
              <div className="form-group">
                <input
                  type="email"
                  className="email-input"
                  placeholder="Enter email address to invite"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                  title="Please enter a valid email address"
                  required
                />
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={actionLoading.sendInvite}
                >
                  {actionLoading.sendInvite ? "Sending..." : "Send Invite"}
                </button>
              </div>
            </form>

            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map(invite => (
                    <tr key={invite.id}>
                      <td>{invite.email}</td>
                      <td>
                        <span className="role-badge">{invite.role || "member"}</span>
                      </td>
                      <td>
                        <button
                          className="btn btn-danger"
                          onClick={() => revokeInvite(invite.id)}
                          disabled={actionLoading[`revoke-${invite.id}`]}
                        >
                          {actionLoading[`revoke-${invite.id}`] ? "Revoking..." : "Revoke"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {invites.length === 0 && (
                    <tr>
                      <td colSpan="3" className="no-data">No pending invites.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
